import React from 'react';
import { Box, Typography, IconButton, Divider } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { COLORS } from '../styles';

// Mock stop list for any previous run, keyed off the run id. For the demo
// we don't need real data per run — just realistic-looking content.
const MOCK_STOPS = [
  { flight: 'WN 1861', dest: 'EUG', gate: 'C37', bags: 7,  arrived: '05:48' },
  { flight: 'WN 1796', dest: 'BOI', gate: 'C49', bags: 4,  arrived: '05:54' },
  { flight: 'WN 2282', dest: 'ABQ', gate: 'C54', bags: 1,  arrived: '06:00' },
  { flight: 'WN 1997', dest: 'PDX', gate: 'C35', bags: 2,  arrived: '06:06' },
  { flight: 'WN 3112', dest: 'ELP', gate: 'C59', bags: 3,  arrived: '06:11' },
  { flight: 'WN 2068', dest: 'SEA', gate: 'C41', bags: 1,  arrived: '06:14' },
];

function RunSummaryScreen({ run, onBack }) {
  const stops = MOCK_STOPS.slice(0, run.stops);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ p: 1.5, borderBottom: `1px solid ${COLORS.divider}`, display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton size="small" onClick={onBack} sx={{ color: COLORS.textSecondary, p: 0.5 }}>
          <ArrowBackIcon sx={{ fontSize: 18 }} />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: '0.6rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Run Summary
          </Typography>
          <Typography sx={{ fontSize: '0.95rem', color: COLORS.textPrimary, fontWeight: 700, lineHeight: 1.1 }}>
            {run.flightNumber} · from {run.from}
          </Typography>
        </Box>
        {run.onTime ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CheckCircleIcon sx={{ color: COLORS.success, fontSize: 14 }} />
            <Typography sx={{ fontSize: '0.6rem', color: COLORS.success, fontWeight: 700 }}>ON TIME</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <WarningAmberIcon sx={{ color: COLORS.warn, fontSize: 14 }} />
            <Typography sx={{ fontSize: '0.6rem', color: COLORS.warn, fontWeight: 700 }}>LATE</Typography>
          </Box>
        )}
      </Box>

      {/* Top stats */}
      <Box sx={{ p: 1.5, display: 'flex', gap: 1 }}>
        <Stat label="Stops" value={run.stops} />
        <Stat label="Bags"  value={run.bags} />
        <Stat label="Mins"  value={run.durationMin} />
      </Box>

      {/* Timing */}
      <Box sx={{ px: 1.5, pb: 1, display: 'flex', justifyContent: 'space-between' }}>
        <TimingPill label="Started" value={run.startedAt} />
        <TimingPill label="Ended"   value={run.endedAt} />
      </Box>

      <Divider sx={{ borderColor: COLORS.divider, mx: 1.5 }} />

      {/* Stops */}
      <Box sx={{ p: 1.5 }}>
        <Typography sx={{ fontSize: '0.6rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, mb: 0.5 }}>
          Stops
        </Typography>
        {stops.map((s, i) => (
          <Box key={i} sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            py: 0.5,
            borderBottom: `1px solid ${COLORS.divider}`,
            '&:last-child': { borderBottom: 'none' },
          }}>
            <Box sx={{
              width: 20, height: 20, borderRadius: '50%',
              backgroundColor: COLORS.surface,
              color: COLORS.accent,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.6rem', fontWeight: 700,
              flexShrink: 0,
            }}>
              {i + 1}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontSize: '0.72rem', color: COLORS.textPrimary, fontWeight: 600 }}>
                {s.flight} → {s.dest}
              </Typography>
              <Typography sx={{ fontSize: '0.58rem', color: COLORS.textMuted }}>
                {s.gate} · {s.bags} bags · arrived {s.arrived}
              </Typography>
            </Box>
            <CheckCircleIcon sx={{ color: COLORS.success, fontSize: 14 }} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function Stat({ label, value }) {
  return (
    <Box sx={{ flex: 1, backgroundColor: COLORS.surface, borderRadius: 1.5, p: 0.85, textAlign: 'center' }}>
      <Typography sx={{ color: COLORS.accent, fontSize: '1rem', fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ color: COLORS.textMuted, fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: 0.5, mt: 0.25 }}>{label}</Typography>
    </Box>
  );
}

function TimingPill({ label, value }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
      <Typography sx={{ fontSize: '0.55rem', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.75rem', color: COLORS.textPrimary, fontWeight: 600 }}>
        {value}
      </Typography>
    </Box>
  );
}

export default RunSummaryScreen;
